import React from 'react'
import { Form } from 'react-bootstrap'
import { type IdentityRequestMethod } from '../../../models/CustomIntegration'
import { type IdentityFormData, isHttpsUrl } from '../form/customIntegrationForm'
import { HeadersFormFields } from './HeadersFormFields'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'

interface IdentityFormFieldsProps {
  identity: IdentityFormData
  onChange: (identity: IdentityFormData) => void
  disabled?: boolean
}

const METHODS: IdentityRequestMethod[] = ['GET', 'POST']
const METHOD_OPTIONS: SelectOption[] = METHODS.map((method) => ({ id: method, label: method }))

export const IdentityFormFields: React.FC<IdentityFormFieldsProps> = ({ identity, onChange, disabled = false }) => {
  const update = (patch: Partial<IdentityFormData>) => onChange({ ...identity, ...patch })
  const url = identity.url.trim()
  const selectedMethod = METHOD_OPTIONS.find((option) => option.id === identity.method) ?? null

  return (
    <div className='d-flex flex-column gap-3'>
      <Form.Group controlId='custom-integration-identity-url'>
        <Form.Label className='paragraph-small mb-1'>
          Identity URL <span className='zocks-text-gray-600'>(optional)</span>
        </Form.Label>
        <Form.Control
          type='url'
          placeholder='e.g. https://acme.xyz/api/user'
          value={identity.url}
          disabled={disabled}
          isInvalid={url !== '' && !isHttpsUrl(url)}
          onChange={(e) => update({ url: e.target.value })}
        />
        <Form.Control.Feedback type='invalid'>Please enter a valid HTTPS URL.</Form.Control.Feedback>
        <Form.Text className='paragraph-extra-small zocks-text-gray-600'>Endpoint to fetch user identity after authentication</Form.Text>
      </Form.Group>

      <Form.Group controlId='custom-integration-account-identifier-path'>
        <Form.Label className='paragraph-small mb-1'>
          Account Identifier <span className='zocks-text-gray-600'>(optional)</span>
        </Form.Label>
        <Form.Control
          type='text'
          placeholder='eg. $.id, $.email, $.user.id'
          value={identity.accountIdentifierPath}
          disabled={disabled}
          onChange={(e) => update({ accountIdentifierPath: e.target.value })}
        />
        <Form.Text className='paragraph-extra-small zocks-text-gray-600'>
          JSONPath to the user identifier in the identity response, such as $.id, $.email, or $.user.id.
        </Form.Text>
      </Form.Group>

      <Form.Group controlId='custom-integration-identity-method'>
        <Form.Label className='paragraph-small mb-1'>HTTP method</Form.Label>
        <SelectDropdown
          toggleId='custom-integration-identity-method'
          options={METHOD_OPTIONS}
          initialSelectedOption={selectedMethod}
          placeholder='Select HTTP method'
          disabled={disabled}
          onOptionSelected={(option) => update({ method: option.id as IdentityRequestMethod })}
        />
        <Form.Text className='paragraph-extra-small zocks-text-gray-600'>The HTTP method used to fetch the user identity. Defaults to GET.</Form.Text>
      </Form.Group>

      <HeadersFormFields headers={identity.headers} onChange={(headers) => update({ headers })} disabled={disabled} />
    </div>
  )
}
